/**
 * [TD-47.1] AuctionActionDrawer - auction's nomination initiation surface.
 *
 * Renders inline below the clicked player row in PlayerPoolWithDrawer.
 * Owns ONLY the nomination-initiation flow (opening-bid input + NOMINATE
 * button). Once nomination opens, control transfers to ActiveNomination
 * in the center pane (preserved bid-control surface).
 *
 * Founder verdicts (TD-47.1 v1.2 lock):
 *   • Gap 1 → B: single-click commit on NOMINATE. No confirmation step.
 *   • Gap C → C.1: dedicated component, clean prop boundary.
 *   • Gap F: state-gating handled at the parent (drawer doesn't open
 *     unless isMyTurnToNominate). This component assumes it's only
 *     mounted when nomination is allowed.
 *
 * Props:
 *   player        - { id, name, position, ... } the row clicked
 *   openingBid    - current opening-bid input value (number)
 *   setOpeningBid - setter for opening bid
 *   onNominate    - single-click commit handler (parent wires to nominate())
 *   onCancel      - closes the drawer without committing
 */
import PositionBadge from "../PositionBadge";

export default function AuctionActionDrawer({
  player,
  openingBid,
  setOpeningBid,
  onNominate,
  onCancel,
}) {
  if (!player) return null;

  const handleBidChange = (e) => {
    const v = parseInt(e.target.value, 10);
    setOpeningBid(Number.isFinite(v) && v > 0 ? v : 1);
  };

  const handleKey = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      onNominate?.();
    } else if (e.key === "Escape") {
      e.preventDefault();
      onCancel?.();
    }
  };

  return (
    <div
      className="border-y border-[#FF6B00]/35 px-3 py-2"
      style={{ background: "rgba(255,107,0,0.08)" }}
      data-testid="auction-action-drawer"
    >
      <div className="flex items-center gap-2 mb-2">
        <PositionBadge position={player.position} size="sm" />
        <p className="font-body text-xs text-[#F5F5F5] leading-tight flex-1 min-w-0">
          Nominating <span className="text-[#FF6B00] font-semibold">{player.name}</span>
        </p>
      </div>
      <div className="flex items-center gap-2">
        <div className="flex items-center gap-1">
          <span className="font-mono text-[10px] text-[rgba(255,255,255,0.45)]">$</span>
          <input
            type="number"
            value={openingBid}
            onChange={handleBidChange}
            onKeyDown={handleKey}
            min={1}
            autoFocus
            className="w-16 bg-[#252525] border border-[#2E2E2E] focus:border-[#FF6B00]/50 text-[#F5F5F5] rounded px-2 py-1 text-xs font-mono outline-none transition-colors"
            data-testid="auction-action-drawer-bid-input"
          />
        </div>
        <button
          onClick={onNominate}
          onKeyDown={handleKey}
          data-testid="auction-action-drawer-nominate-btn"
          className="flex-1 bg-[#FF6B00] hover:bg-[#FF8533] text-white font-display font-black text-[11px] uppercase tracking-wider px-3 py-1.5 rounded transition-all active:scale-95"
        >
          Nominate
        </button>
        <button
          onClick={onCancel}
          data-testid="auction-action-drawer-cancel-btn"
          className="text-[rgba(255,255,255,0.50)] hover:text-[#F5F5F5] font-body text-xs px-2 transition-colors"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
